import '../styles/style.scss';
import videojs from 'video.js';
import Tools from './services/tools';
import PostersManager from './services/postersManager';
import PlayerWrapper from './playerWrapper';
import PlayerSrv from './services/playerSrv';
import { screenMode } from 'vimmi-web-utils/cjs/screenMode';
import Restriction from './services/restriction';
import { SnapshotService } from './vjsComponents/snapshotjs';
import { CutVideoService } from './vjsComponents/cut-video';
import './vjsComponents/custom-audio-tracks-selector';
import './vjsComponents/custom-big-play-button';
import Config from './services/config';
import eventsSrv, { offSubscribers, subscribeByTarget } from '../js/services/events';


import Analytics from './plugins/analytics';
import vimmiPI from './plugins/vimmiPI';
import vjsSrv from './services/vjsSrv';
import Preview from './services/preview';
import AudioTracksSrv from './services/audioTracksSrv';
import lifecycle from 'page-lifecycle';
import { secondLocation } from 'vimmi-web-utils/cjs/secondLocation';
import autoplayState from './services/autoplayState';
import { PLAYER_EVENTS, PLAYER_METHODS, PM } from 'vimmi-web-utils/cjs/postMessage';
import { PMWrap } from './services/PMWrap';
import { ErrorCodes } from 'vimmi-web-utils/cjs/errors/errorCodes';
import { thirdLocation } from 'vimmi-web-utils/cjs/thirdLocation';
import ads from './plugins/ads';
import { fetchAMS } from 'vimmi-web-utils/cjs/ams/fetchAMS';
import { deviceAsync } from 'vimmi-web-utils/cjs/deviceAsync';
import $logger from 'src/js/logger';
import StyledLogger from 'vimmi-web-utils/cjs/logger';

export default class VimmiPlayer {
  autoplayState = autoplayState;
  vimmiPI = vimmiPI;
  Preview = Preview;
  playerWrapper = new PlayerWrapper();
  ads = ads;

  one(eventName, handler) {
    return eventsSrv.one(eventName, handler);
  };

  on(eventName, handler) {
    return eventsSrv.on(eventName, handler);
  };

  off(eventName, handler) {
    return eventsSrv.off(eventName, handler);
  };

  /**
   * @param tagId {string}  video tag id on the page
   * @param options {Object} player initial options
   * @param options.imported {Boolean} define does player imported into another app as module, if true, postMessages will not works
   * @param options.account {String} customer account
   * @param options.qualityChange {Boolean} @deprecated - use "controlBar" instead. show or hide quality selector
   * @param options.fullScreen {Boolean} @deprecated - use "controlBar" instead. show or hide fullScreen toggle button
   * @param options.showEmbedded {Boolean} @deprecated - use "controlBar" instead. show or hide showEmbedded toggle button
   * @param options.mute {Boolean} @deprecated - use "muted" instead. mute unmute initial play
   * @param options.muted {Boolean} mute or unmute initial play, default unmuted
   * @param options.debug {Boolean} enable/disable debug mode
   * @param options.lang {String} player ui lang
   * @param options.screenType? {'bs'|'ss'} screen type for third location
   * @param options.platform? {'stv'|'web'} platform type for 3rdLocation adn 2ndLocation
   * @param options.dash {Object} dashJs options
   * @param options.useTag {Element} video tag, will used instead creating new one
   * @param options.initEmpty {Boolean} init player as empty
   * @param options.playOnWrapClick {Boolean} if true - will start to play by clicking on bigPlayBtn wrapper
   * @param options.controls {Boolean} init player with controls
   * @param options.hotkeys {Boolean} enable hotkey supports
   * @param options.hotkeys {Boolean} enable hotkey supports
   * @param options.trackLiveTime {Boolean} does player need to try tracking live position (parsing hls response to know on which position the player)
   * @param options.controlBar {Object} json to configure player control bar
   * @param options.plugins {Object} json to configure player plugins
   * @param options.videoJsOptions {Object}
   * @param options.videoJsOptions.html5 {Object}  details https://docs.videojs.com/tutorial-options.html#html5
   * @param options.videoJsOptions.dash {Object}  todo
   * @param options.videoJsOptions.GOAL_BUFFER_LENGTH {number}
   * @param options.videoJsOptions.MAX_GOAL_BUFFER_LENGTH {number}
   * for AIS
   * @param options.sl {Boolean} use second location, need only for AIS
   * @param options.tl {Boolean} use third location, need only for AIS
   * */
  constructor(tagId, options = null) {
    $logger.force().groupCollapsed(`VimmiPlayer Version: ${process.env.version}`);
    $logger.force().info(`Build Date: ${process.env.buildDate}`);
    $logger.force().info(`VideoJs Version: ${videojs.VERSION}`);
    $logger.force().groupEnd();

    options = options || {};
    options.controlBar = options.controlBar || {};

    this.imported = options.imported;

    if (options.mute !== undefined) {
      options.muted = !!options.mute;
      delete options.mute;
    }


    if (options.controlBar.AudioTrackButton === undefined) {
      options.controlBar.AudioTrackButton = true;
    }
    if (options.controlBar.QualitySelectorButton === undefined) {
      options.controlBar.QualitySelectorButton = true;
    }
    if (options.controlBar.FullscreenToggle === undefined) {
      options.controlBar.FullscreenToggle = true;
    }
    if (options.controlBar.SubtitlesButton === undefined) {
      options.controlBar.SubtitlesButton = true;
    }

    // ---------- for old version start
    // if (options.showEmbedded) {
    //   options.controlBar.EmbeddedButton = !!options.showEmbedded;
    //   delete options.showEmbedded;
    // }
    if (options.fullScreen !== undefined) {
      options.controlBar.FullscreenToggle = !!options.fullScreen;
      delete options.fullScreen;
    }
    if (options.qualityChange !== undefined) {
      options.controlBar.QualitySelectorButton = !!options.qualityChange;
      delete options.qualityChange;
    }
    // ---------- for old version end

    options.controls = options.controls === undefined ? true : options.controls;
    options.hotkeys = options.hotkeys === undefined ? true : options.hotkeys;

    Config.set('playerId', tagId);
    Config.set('params', options);
    Config.set('debug', Config.get('params.debug'));
    Config.set('screenType', options?.screenType || 'ss');
    Config.set('platform', options?.platform || 'web');
    Config.set('params.env', Config.get('params.env') || 'prod');


    let debugLevel = Config.get('params.debug');
    if (!isNaN(parseInt(debugLevel))) {
      $logger.config.set('debug.level', debugLevel);
    } else {
      $logger.config.set('debug.level', Config.get('debug') ? StyledLogger.DEBUG_LEVEL : StyledLogger.TRACE_LEVEL);
    }


    vjsSrv.initVideoJsHooks();
    this._init({
      videoJsOptions: options.videoJsOptions,
      controls: options.controls,
      hotkeys: options.hotkeys,
      imported: options.imported,
      plugins: options.plugins || null,
      controlBar: options && options.controlBar ? options.controlBar : null,
      useTag: options && options.useTag ? options.useTag : null
    });
  }

  async _init(options, secondInit = false) {

    if (!options.imported && Config.get('params.sl')) {
      secondLocation.disable(); // just for clear localStorage
      secondLocation.setEnv(Config.get('params.env'));
      secondLocation.setScreenType(Config.get('screenType'));
      secondLocation.setPlatform(Config.get('platform'));
      secondLocation.enable();
    }

    if (!options.imported && Config.get('params.tl')) {
      thirdLocation.disable(); // just for clear localStorage
      thirdLocation.set('env', Config.get('params.env'));
      thirdLocation.set('platform', Config.get('platform'));
      thirdLocation.events.on('updatedDomain', () => {
        PMWrap.inst().sendWrappedEvent(PLAYER_METHODS.CONFIGURATION, {
          thirdLocation: {
            activeDomain: thirdLocation.get('activeDomain'),
            domains: thirdLocation.get('domains'),
            nRetries: thirdLocation.get('nRetries'),
            nNonPriorityRetries: thirdLocation.get('nNonPriorityRetries'),
            nDistroRetries: thirdLocation.get('nDistroRetries'),
            confRetryDelay: thirdLocation.get('confRetryDelay'),
            confAttempts: thirdLocation.get('confAttempts'),
            maxReq: thirdLocation.get('maxReq')
          }
        });
      });
      thirdLocation.events.on('thirdLocationRetry', () => {
        PMWrap.inst().sendWrappedEvent(PLAYER_METHODS.CONFIGURATION, {
          thirdLocation: {
            activeDomain: thirdLocation.get('activeDomain'),
            domains: thirdLocation.get('domains'),
            nRetries: thirdLocation.get('nRetries'),
            nNonPriorityRetries: thirdLocation.get('nNonPriorityRetries'),
            nDistroRetries: thirdLocation.get('nDistroRetries'),
            confRetryDelay: thirdLocation.get('confRetryDelay'),
            confAttempts: thirdLocation.get('confAttempts'),
            maxReq: thirdLocation.get('maxReq'),
            triggerRetry: true
          }
        });
      });
      await thirdLocation.enable(true);
    }

    return vjsSrv.createPlayer(options)
      .then(async (vjsPlayer) => {
        this.vjsPlayer = vjsPlayer;
        this._handleWindowEvent();
        if (!this.imported) {
          this._handlePMEvents();
        }
        this._handlePlayerEvents();
        PostersManager.init();

        vjsPlayer.removeClass('before-init');
        if (Config.get('params.initEmpty')) {
          vjsPlayer.addClass('empty-player');
        } else {
          let asset = PlayerSrv.transformToAsset(Config.get('params'));
          this.playerWrapper.play(asset, false);
        }
        if (await deviceAsync.isSafari()) {
          vjsPlayer.addClass('non-ima-fullscreen');
        }
        if (!secondInit) {
          $logger.p('ready').info(Config.get('params'));
        }


        if (lifecycle.state !== 'terminated' && !secondInit) {
          let eventData = {};
          eventsSrv.emit(PLAYER_EVENTS.READY, eventData);
          if (!Config.get('params.imported')) {
            PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.READY, eventData);
          }
        }
      });
  }


  _handleWindowEvent() {
    vjsSrv.vjsPlayer.addClass(`screen-${screenMode()}`);
    subscribeByTarget('click', window, (evt) => {
      eventsSrv.emit(PLAYER_EVENTS.AREA_CLICK);
      if (!Config.get('params.imported')) {
        PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.AREA_CLICK);
      }
    }, true);
    subscribeByTarget('orientationchange', window, () => {
      Analytics.send('media_screen_switch');
      eventsSrv.emit(PLAYER_EVENTS.ORI_CAHNGE);
      if (!Config.get('params.imported')) {
        PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.ORI_CAHNGE);
      }

      vjsSrv.vjsPlayer.removeClass(`screen-portrait`);
      vjsSrv.vjsPlayer.removeClass(`screen-landscape`);
      vjsSrv.vjsPlayer.addClass(`screen-${screenMode()}`);
    });
    subscribeByTarget('resize', window, () => {
      eventsSrv.emit(PLAYER_EVENTS.RESIZE);
      if (!Config.get('params.imported')) {
        PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.RESIZE);
      }
      vjsSrv.vjsPlayer.removeClass(`screen-portrait`);
      vjsSrv.vjsPlayer.removeClass(`screen-landscape`);
      vjsSrv.vjsPlayer.addClass(`screen-${screenMode()}`);
    });
    let isPaused = false;
    subscribeByTarget('statechange', lifecycle, async (event) => {
      if (event.newState === 'hidden') {
        if (await deviceAsync.isAnyMobile() && !this.imported) {
          if (Config.get('eventsState.isCallPlay')) {
            vjsSrv.onPaused(false);
            vjsSrv.vjsPlayer.pause();
            isPaused = true;
          }
        }
      } else if (event.newState === 'active' && !this.imported) {
        if (await deviceAsync.isAnyMobile() && isPaused) {
          if (Config.get('eventsState.isCallPlay')) {
            vjsSrv.onStarted(false);
            vjsSrv.vjsPlayer.play();
            isPaused = false;
          }
        }
      } else if (event.newState === 'terminated') {
        if (vimmiPI.getActiveAsset() && Config.get('eventsState.started') && !Config.get('eventsState.ended')) {
          Config.set('eventsState.ended', true);

          Analytics.send('progress', {}, {
            only: ['sati', 'ga']
          });

          Analytics.send('chu', {}, { force: true });
          Analytics.send('stop', {
            stop_cause: 'app focus lost'
          }, {
            beacon: true,
            fetch: false
          });
        } else {
          Analytics.beforePageClose({
            beacon: true,
            fetch: false
          });
        }
        if (Config.get('eventsState.started')) {
          vimmiPI.concurStop(true);
        }
      }
    });
    // lifecycle.addEventListener('statechange', stateChange);
    // this.eventsSubscribers.push({ event: 'statechange', fn: stateChange, target: lifecycle });
  }

  // подписка на все ивенты от веб-портала
  _handlePMEvents() {

    PMWrap.inst().on(PLAYER_METHODS.MUTE, (event) => {
      this.controls.mute(event);
    });
    PMWrap.inst().on(PLAYER_METHODS.PLAY, (event) => {
      this.controls.play(event?.data?.userAction);
    });
    PMWrap.inst().on(PLAYER_METHODS.PAUSE, (event) => {
      this.controls.pause(event?.data?.userAction);
    });
    PMWrap.inst().on(PLAYER_METHODS.PLAY_NEW_VIDEO, (event) => {
      this.playVideo(event);
    });

    PMWrap.inst().on(PLAYER_METHODS.SET_RESTRICTION, (event) => {
      this.setRestriction(event);
    });
    PMWrap.inst().on(PLAYER_METHODS.TOGGLE_PREVIEW, (event) => {
      this.togglePreview(event);
    });

    /** BUTTONS_ACT !DEPRECATED **/
    PMWrap.inst().on(PLAYER_METHODS.BUTTONS_ACT, (event) => {
      this.controls.controlsState(event);
    });

    PMWrap.inst().on(PLAYER_METHODS.CONTROLS_STATE, (event) => {
      this.controls.controlsState(event);
    });


    PMWrap.inst().on(PLAYER_METHODS.CONFIGURATION, (event) => {
      this.configure(event.data);
    });

    /** Cut video tool */

    PMWrap.inst().on(PLAYER_METHODS.CUT_VIDEO_API, this.controls.cutAPI);

    /** SNAPSHOT functionality */
    PMWrap.inst().on(PLAYER_METHODS.SNAPSHOT_API, this.controls.snapshotAPI);


    PMWrap.inst().on('spec_pause', () => {
      if (!vimmiPI.getActiveAsset()) {
        return;
      }
      if (vimmiPI.getActiveAsset().get('extraData.errorHandlerTimeoutId')) {
        clearTimeout(vimmiPI.getActiveAsset().get('extraData.errorHandlerTimeoutId'));
      }
      vjsSrv.vjsPlayer.pause();
      vjsSrv.vjsPlayer.hasStarted(false);
      vjsSrv.vjsPlayer.currentTime(0);
      vjsSrv.vjsPlayer.reset();
      PostersManager.setCurrentPoster(vimmiPI.getActiveAsset());
    });

    PMWrap.inst().on('spec_play', () => {
      if (!vimmiPI.getActiveAsset()) {
        return;
      }
      vimmiPI.getActiveAsset().playerOptions.autoplay = true;
      if (vimmiPI.getActiveAsset().get('extraData.errorHandlerTimeoutId')) {
        clearTimeout(vimmiPI.getActiveAsset().get('extraData.errorHandlerTimeoutId'));
      }
      this.playVideo(vimmiPI.getActiveAsset());
    });

  }

  _handlePlayerEvents() {
    let _timeUpdate_prevTime = 0;
    /* player event */
    vjsSrv.vjsPlayer
      .on('fullscreenchange', () => {
        eventsSrv.emit(PLAYER_EVENTS.FULLSCR_CHANGE);
        if (!Config.get('params.imported')) {
          PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.FULLSCR_CHANGE);
        }
      });

    vjsSrv.vjsPlayer
      .on('loadstart', () => {
        $logger.p('event').info('loadstart', {
          duration: vjsSrv.vjsPlayer.duration(),
          currentTime: vjsSrv.vjsPlayer.currentTime(),
          eventsState: Config.get('eventsState'),
          state: vimmiPI.getActiveAsset() ? vimmiPI.getActiveAsset().get('state') : {}
        });
        if (!vimmiPI.getActiveAsset()) {
          return;
        }
        AudioTracksSrv.disableCustomButton();
        if (!vimmiPI.getActiveAsset().get('state.ready')) {
          vjsSrv.vjsPlayer.addClass('custom-waiting-video');
        } else {
          if (!vjsSrv.vjsPlayer.ads?._shouldBlockPlay) {
            vjsSrv.vjsPlayer.removeClass('custom-waiting-video');
          }
        }

        Config.set('eventsState.error', false);
        if (vimmiPI.getActiveAsset()) {
          vimmiPI.getActiveAsset().set('extraData.n_streams', 0);
          vimmiPI.getActiveAsset().set('extraData.max_bitrate', 0);
          vimmiPI.getActiveAsset().set('extraData.min_bitrate', 0);
        }

        Config.set('eventsState.sourceChanging', false);
      });
    vjsSrv.vjsPlayer
      .on('durationchange', async (event) => {
        $logger.p('event').info('durationchange', {
          duration: vjsSrv.vjsPlayer.duration(),
          currentTime: vjsSrv.vjsPlayer.currentTime(),
          eventsState: Config.get('eventsState'),
          state: vimmiPI.getActiveAsset() ? vimmiPI.getActiveAsset().get('state') : {}
        });
        if (!vimmiPI.getActiveAsset()) {
          return;
        }
        eventsSrv.emit('durationchange');
        if (!Config.get('params.imported')) {
          PMWrap.inst().sendWrappedEvent('durationchange');
        }

        if (vimmiPI.getActiveAsset().get('isLive')) {

          /** Fix android bug. Live starts from 0 position */
          if (await deviceAsync.isAndroid() && Config.get(`params.portal.customer`) === 'verb') {
            vjsSrv.vjsPlayer.currentTime(vjsSrv.vjsPlayer.seekable().end(0));
          }

          if (!(await vjsSrv.autoplayBrowserAllow() || await vjsSrv.mutedAutoplayBrowserAllow())) {
            if (!vjsSrv.vjsPlayer.ads?._shouldBlockPlay) {
              vjsSrv.vjsPlayer.removeClass('custom-waiting-video');
            }
          }
        }

        // vjsSrv.vjsPlayer.removeClass('pre-loading');
        /** it means that some data loaded */
        if (vimmiPI.getActiveAsset().get('isLive') && (vjsSrv.vjsPlayer.duration() === Infinity || vjsSrv.vjsPlayer.duration() === NaN) && await deviceAsync.isFirefox()) {
          eventsSrv.emit(PLAYER_EVENTS.LOADEDMETADATA);
          if (!Config.get('params.imported')) {
            PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.LOADEDMETADATA);
          }

          /** if it is live stream it just show play button
           * and sends necessary events */
          if (!vimmiPI.getActiveAsset().get('extraData.delay_end')) {
            vimmiPI.getActiveAsset().set('extraData.delay_end', Date.now());
          }
          if (!vimmiPI.getActiveAsset().get('extraData.first_byte_end')) {
            vimmiPI.getActiveAsset().set('extraData.first_byte_end', Date.now());
          }
          if (!vimmiPI.getActiveAsset().get('state.ready')) {
            vimmiPI.getActiveAsset().set('state.ready', true);
            setTimeout(() => {
              if (!Restriction.isRestricted(vimmiPI.getActiveAsset())) {
                Analytics.send('player_ready', {}, { only: ['buh'] });
              }
            });
          }

          this.playerWrapper.applyStartPosition();

          if (await deviceAsync.isSafari() && !(ads.playing() && ads.ad.isLinear())) {
            vjsSrv.tryPlay();
          }
        }
        // if (vjsSrv.vjsPlayer.paused()) {
        //   this.vjsPlayer.removeClass('vjs-playing');
        //   this.vjsPlayer.addClass('vjs-paused');
        // }
      });
    vjsSrv.vjsPlayer
      .on('loadedmetadata', async () => {
        $logger.p('event').info('loadedmetadata', {
          duration: vjsSrv.vjsPlayer.duration(),
          currentTime: vjsSrv.vjsPlayer.currentTime(),
          eventsState: Config.get('eventsState'),
          state: vimmiPI.getActiveAsset() ? vimmiPI.getActiveAsset().get('state') : {}
        });
        if (!vimmiPI.getActiveAsset()) {
          return;
        }
        Analytics.onTimeUpdate(vimmiPI.getActiveAsset());

        Config.set('eventsState.loadedmetadata', true);

        if (vjsSrv.vjsPlayer.duration() !== Infinity && vjsSrv.vjsPlayer.duration() !== NaN) {
          if (await deviceAsync.isSafari()) {
            if (!vimmiPI.getActiveAsset().get('extraData.delay_end')) {
              vimmiPI.getActiveAsset().set('extraData.delay_end', Date.now());
            }
            if (!vimmiPI.getActiveAsset().get('extraData.first_byte_end')) {
              vimmiPI.getActiveAsset().set('extraData.first_byte_end', Date.now());
            }
            if (!vimmiPI.getActiveAsset().get('state.changingAudio')) {
              eventsSrv.emit(PLAYER_EVENTS.LOADEDMETADATA);
              if (!Config.get('params.imported')) {
                PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.LOADEDMETADATA);
              }
            }
            if (!(ads.playing() && ads.ad.isLinear())) {
              vjsSrv.tryPlay();
            }
          }

        } else {
          if (await deviceAsync.isSafari() && !(ads.playing() && ads.ad.isLinear())) {
            vjsSrv.tryPlay();
          }
        }
        if (!vimmiPI.getActiveAsset().get('state.ready')) {
          vimmiPI.getActiveAsset().set('state.ready', true);
          setTimeout(() => {
            if (!Restriction.isRestricted(vimmiPI.getActiveAsset())) {
              Analytics.send('player_ready', {}, { only: ['buh'] });
            }
          });
        }

        if (!vjsSrv.vjsPlayer.ads?._shouldBlockPlay) {
          vjsSrv.vjsPlayer.removeClass('custom-waiting-video');
        }

      });
    vjsSrv.vjsPlayer
      .on('loadeddata', async (event) => {
        $logger.p('event').info('loadeddata', {
          duration: vjsSrv.vjsPlayer.duration(),
          currentTime: vjsSrv.vjsPlayer.currentTime(),
          eventsState: Config.get('eventsState'),
          state: vimmiPI.getActiveAsset() ? vimmiPI.getActiveAsset().get('state') : {}
        });

        if (!vimmiPI.getActiveAsset()) {
          return;
        }
        if (vjsSrv.vjsPlayer.paused() && vjsSrv.vjsPlayer.el().classList.contains('vjs-playing')) {
          /** to fix missed play button*/
          vjsSrv.vjsPlayer.el().classList.replace('vjs-playing', 'vjs-paused');
        }

        Config.set('eventsState.loadeddata', true);

        if (!vjsSrv.vjsPlayer.ads?._shouldBlockPlay) {
          vjsSrv.vjsPlayer.removeClass('custom-waiting-video');
        }
        if (vjsSrv.vjsPlayer.duration() !== Infinity && vjsSrv.vjsPlayer.duration() !== NaN) {
          if (!(await deviceAsync.isSafari())) {
            if (!vimmiPI.getActiveAsset().get('extraData.delay_end')) {
              vimmiPI.getActiveAsset().set('extraData.delay_end', Date.now());
            }
            if (!vimmiPI.getActiveAsset().get('extraData.first_byte_end')) {
              vimmiPI.getActiveAsset().set('extraData.first_byte_end', Date.now());
            }
            if (!vimmiPI.getActiveAsset().get('state.changingAudio')) {
              eventsSrv.emit(PLAYER_EVENTS.LOADEDMETADATA);
              if (!Config.get('params.imported')) {
                PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.LOADEDMETADATA);
              }

            }
          }
        }

        if (!vimmiPI.getActiveAsset().get('state.ready')) {
          vimmiPI.getActiveAsset().set('state.ready', true);
          setTimeout(() => {
            if (!Restriction.isRestricted(vimmiPI.getActiveAsset())) {
              Analytics.send('player_ready', {}, { only: ['buh'] });
            }
          });
        }
      });

    // vjsSrv.vjsPlayer
    //   .on('volumechange', () => {
    //     $logger.p('event').info('volumechange', {
    //         isMuted: vjsSrv.vjsPlayer.muted(),
    //         volume: vjsSrv.vjsPlayer.volume()
    //       });
    //   });

    // when player start trying play
    vjsSrv.vjsPlayer
      .on('play', async (event) => {
        $logger.p('event').info('play', {
          duration: vjsSrv.vjsPlayer.duration(),
          currentTime: vjsSrv.vjsPlayer.currentTime(),
          eventsState: Config.get('eventsState'),
          state: vimmiPI.getActiveAsset() ? vimmiPI.getActiveAsset().get('state') : {}
        });
        /** in webrtc stream case */
        Config.set('eventsState.isCallPlay', true);

        if (!vimmiPI.getActiveAsset()) {
          return;
        }

        if (Restriction.isRestricted(vimmiPI.getActiveAsset())) {
          Restriction.skipPlay();
        }

        if (vimmiPI.getActiveAsset().isLive) {
          // console.log(vjsSrv.vjsPlayer.seekable(), {
          //   current: vjsSrv.vjsPlayer.currentTime(),
          //   seekEnd: vjsSrv.vjsPlayer.seekable().length ? vjsSrv.vjsPlayer.seekable().end(0) : null
          // });
          if (vjsSrv.vjsPlayer.seekable().length) { // to seek to current position after each time when live paused
            const seekTime = vjsSrv.vjsPlayer.seekable().end(0);

            /** bug related to macOS 15.4 ndvr*/
            {
              vjsSrv.vjsPlayer.one('seeked', () => {
                if (vjsSrv.vjsPlayer.currentTime() < seekTime) {
                  vjsSrv.vjsPlayer.currentTime(vjsSrv.vjsPlayer.seekable().end(0));
                }
              });
              if (vjsSrv.vjsPlayer.currentTime() < seekTime) {
                vjsSrv.vjsPlayer.currentTime(vjsSrv.vjsPlayer.seekable().end(0));
              }
            }
          }
        }
      });

    vjsSrv.vjsPlayer
      .on('pause', () => {
        $logger.p('event').info('pause', {
          duration: vjsSrv.vjsPlayer.duration(),
          currentTime: vjsSrv.vjsPlayer.currentTime(),
          eventsState: Config.get('eventsState'),
          state: vimmiPI.getActiveAsset() ? vimmiPI.getActiveAsset().get('state') : {}
        });

        if (!vimmiPI.getActiveAsset()) {
          return;
        }

        vjsSrv.onPaused(false);
      });
    //player real start playing
    let lastSeekEvent = null;
    vjsSrv.vjsPlayer
      .on('playing', () => {
        $logger.p('event').info('playing', {
          duration: vjsSrv.vjsPlayer.duration(),
          currentTime: vjsSrv.vjsPlayer.currentTime(),
          eventsState: Config.get('eventsState'),
          state: vimmiPI.getActiveAsset() ? vimmiPI.getActiveAsset().get('state') : {}
        });

        if (!vimmiPI.getActiveAsset()) {
          return;
        }

        if (!vjsSrv.vjsPlayer.ads?._shouldBlockPlay) {
          vjsSrv.vjsPlayer.removeClass('custom-waiting-video');
        }

        if (vjsSrv.vjsPlayer
          .currentTime() !== 0) {
          eventsSrv.emit(PLAYER_EVENTS.PLAYING);
          if (!Config.get('params.imported')) {
            PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.PLAYING);
          }
        }

        this.playerWrapper.applyStartPosition();

        vjsSrv.onStarted(false);
        lastSeekEvent = null;

        if (!vimmiPI.getActiveAsset().get('state.playing')) {
          vimmiPI.getActiveAsset().set('state.playing', true);
          Analytics.send('media_is_playing');
        }
      });
    // NOTE: seeking - Fired whenever the player is jumping to a new time
    // https://docs.videojs.com/player#seeking
    vjsSrv.vjsPlayer
      .on('seeking', (event) => {
        if (!vimmiPI.getActiveAsset()) {
          return;
        }

        $logger.p('event').info('seeking', {
          duration: vjsSrv.vjsPlayer.duration(),
          currentTime: vjsSrv.vjsPlayer.currentTime(),
          eventsState: Config.get('eventsState'),
          state: vimmiPI.getActiveAsset() && vimmiPI.getActiveAsset().get('state')
        });

        if (!vimmiPI.getActiveAsset().get('state.pause') && vimmiPI.getActiveAsset().get('state.started') && !Config.get('eventsState.seeking')) {
          vimmiPI.getActiveAsset().set('state.pause', true);
          Analytics.send('chu', {}, { force: true });
          Analytics.send('media_pause', {
            position: !vimmiPI.getActiveAsset().isLive ? 0 : Tools.fixTo(vimmiPI.getActiveAsset().get('extraData.active_position') || 0), //overwrite with last position because player on this event got new position
            stop_cause: 'seeking'
          });
        }
        Config.set('eventsState.seeking', true);
      });
    vjsSrv.vjsPlayer
      .on('seeked', async () => {
        const from = vimmiPI.getActiveAsset() && vimmiPI.getActiveAsset().get('extraData.active_position');

        $logger.p('event').info('seeked', {
          from,
          duration: vjsSrv.vjsPlayer.duration(),
          currentTime: vjsSrv.vjsPlayer.currentTime(),
          eventsState: Config.get('eventsState'),
          state: vimmiPI.getActiveAsset() && vimmiPI.getActiveAsset().get('state')
        });

        // issue with cycled seeking on Tizen 3.5
        if (deviceAsync.isSmartTv() && deviceAsync.isTizenOS() && parseInt(await deviceAsync.getOsVersion()) < 4) {
          if (lastSeekEvent && Math.ceil(lastSeekEvent.from) === Math.ceil(vjsSrv.vjsPlayer.currentTime())) {
            vjsSrv.vjsPlayer.pause();
            setTimeout(() => {
              lastSeekEvent = null
              vjsSrv.vjsPlayer.play();
            });
          } else {
            lastSeekEvent = {
              from,
              duration: vjsSrv.vjsPlayer.duration(),
              currentTime: vjsSrv.vjsPlayer.currentTime()
            };
          }
        }

        if (!vimmiPI.getActiveAsset()) {
          return;
        }

        vimmiPI.getActiveAsset().set('extraData.position_from', vimmiPI.getActiveAsset().get('extraData.active_position'));
        vimmiPI.getActiveAsset().set('extraData.active_position', vjsSrv.vjsPlayer.currentTime());
        if (!vimmiPI.getActiveAsset().isLive && Config.get('eventsState.seeking') && !Preview.ended) {
          Analytics.send('seek');
        }

        if (vimmiPI.getActiveAsset().get('state.pause') && vimmiPI.getActiveAsset().get('state.started') && Config.get('eventsState.seeking')) {
          // vjsSrv.onPlayerStart();
          vimmiPI.getActiveAsset().set('state.pause', false);
        }

        if (Math.ceil(vjsSrv.vjsPlayer.duration()) <= Math.ceil(vjsSrv.vjsPlayer.currentTime() + 0.5)) {
          vjsSrv.vjsPlayer.trigger('ended');
        }

        if (Config.get(`params.portal.customer`) === 'verb') {
          if (vimmiPI.getActiveAsset().isLive && vjsSrv.vjsPlayer.duration() === Infinity && vjsSrv.vjsPlayer.currentTime() < from) {
            vjsSrv.vjsPlayer.currentTime(vjsSrv.vjsPlayer.seekable().end(0));
          }
        }

        Config.set('eventsState.seeking', false);
      });

    vjsSrv.vjsPlayer
      .on('timeupdate', () => {
        if (!vimmiPI.getActiveAsset()) {
          return;
        }

        let stats = vjsSrv.vjsPlayer.TechSrv.stats;
        vimmiPI.getActiveAsset().set('extraData.active_bitrate', stats.streamBitrate || stats.averageBitrate || vimmiPI.getActiveAsset().get('extraData.active_bitrate') || 0);
        vimmiPI.getActiveAsset().set('extraData.bytes', stats.bytesReceived || 0);

        if (vimmiPI.getActiveAsset().get('state.changingAudio') && vimmiPI.getActiveAsset().get('playerOptions.startPosition')) {
          return;
        }

        /** hook for detect seeking, bug with triggering analytics(catchmedia) event before seeking and seekend*/
        let stream_time = vimmiPI.getActiveAsset().get('extraData.stream_time', 0);
        let current = vjsSrv.vjsPlayer.currentTime();
        let from = vimmiPI.getActiveAsset().get('extraData.active_position') || current;
        const GAP = deviceAsync.isSmartTv() ? 6 : 2;
        // $logger.p('event').info('timeupdate', {
        //   from,
        //   stream_time,
        //   current,
        //   GAP,
        //   rule: (from < current && (from + GAP < current)) || (from > current && (from - GAP > current)),
        //   duration: vjsSrv.vjsPlayer.duration(),
        //   currentTime: vjsSrv.vjsPlayer.currentTime(),
        //   eventsState: Config.get('eventsState'),
        //   state: vimmiPI.getActiveAsset() && vimmiPI.getActiveAsset().get('state')
        // });
        if ((from < current && (from + GAP < current)) || (from > current && (from - GAP > current))) {
          Config.set('eventsState.customSeeking', true);
          Config.set('eventsState.seeking', true);
        } else {
          if (Config.get('eventsState.customSeeking')) {
            Config.set('eventsState.customSeeking', false);
            Config.set('eventsState.seeking', false);
          }
          stream_time += (current - from);
        }
        vimmiPI.getActiveAsset().set('extraData.stream_time', stream_time);
        Analytics.onTimeUpdate(vimmiPI.getActiveAsset());
        if (!Config.get('eventsState.seeking')) {
          vimmiPI.getActiveAsset().set('extraData.active_position', vjsSrv.vjsPlayer
            .currentTime());
        }
        vimmiPI.getActiveAsset().set('extraData.position_to', vjsSrv.vjsPlayer
          .currentTime());

        Config.set('eventsState.prevUserActiveState', vjsSrv.vjsPlayer
          .userActive());

        let currentTime = vjsSrv.vjsPlayer.currentTime() || 0.1;

        if (Math.floor(currentTime) !== _timeUpdate_prevTime && vimmiPI.getActiveAsset()) {

          if (!vjsSrv.vjsPlayer.paused()) {
            Analytics.send('chu');
          }
          eventsSrv.emit(PLAYER_EVENTS.TIME_UPDATE, {
            positionPrev: _timeUpdate_prevTime,
            stream_time: vimmiPI.getActiveAsset().get('extraData.stream_time'),
            position: vjsSrv.vjsPlayer.currentTime(),
            duration: vjsSrv.vjsPlayer.duration(),
            type: vjsSrv.vjsPlayer.currentType(),
            src: vjsSrv.vjsPlayer.currentSrc()
          });
          if (!Config.get('params.imported')) {
            PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.TIME_UPDATE, {
              positionPrev: _timeUpdate_prevTime,
              stream_time: vimmiPI.getActiveAsset().get('extraData.stream_time')
            });
          }
          _timeUpdate_prevTime = Math.floor(currentTime);
        }


      });
    vjsSrv.vjsPlayer
      .on('waiting', async () => {
        $logger.p('event').info('waiting', {
          duration: vjsSrv.vjsPlayer.duration(),
          currentTime: vjsSrv.vjsPlayer.currentTime(),
          eventsState: Config.get('eventsState'),
          state: vimmiPI.getActiveAsset() ? vimmiPI.getActiveAsset().get('state') : {}
        });
        Config.set('eventsState.waiting', true);

        if (deviceAsync.isSmartTv() && deviceAsync.isTizenOS()) {
          if (vjsSrv.vjsPlayer.duration() && vjsSrv.vjsPlayer.duration() !== Infinity && Math.ceil(vjsSrv.vjsPlayer.duration()) <= Math.ceil(vjsSrv.vjsPlayer.currentTime() + 0.5)) {
            const osVersion = await deviceAsync.getOsVersion();
            if (Math.floor(+osVersion).toString() === '3') {
              vjsSrv.vjsPlayer.trigger('ended');
            }
          }
        }

        if (!vimmiPI.getActiveAsset()) {
          return;
        }
        if (!vimmiPI.getActiveAsset().get('extraData.start_buffering')) {
          vimmiPI.getActiveAsset().set('extraData.start_buffering', Date.now());
          Analytics.send('media_buffering_start');
        }
        eventsSrv.emit(PLAYER_EVENTS.WAITING);
        if (!Config.get('params.imported')) {
          PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.WAITING);
        }
      });

    vjsSrv.vjsPlayer
      .on('ended', (event) => {
        vimmiPI.concurStop();
        if (!Config.get('eventsState.ended') && Config.get('eventsState.started')) {
          Config.set('eventsState.ended', true);
          $logger.p('event').info('ended', {
            duration: vjsSrv.vjsPlayer.duration(),
            currentTime: vjsSrv.vjsPlayer.currentTime(),
            eventsState: Config.get('eventsState'),
            state: vimmiPI.getActiveAsset() ? vimmiPI.getActiveAsset().get('state') : {}
          });
          Analytics.send('progress', {}, { only: ['sati', 'ga'] });
          Analytics.send('chu', {}, { force: true });
          Analytics.send('stop', { stop_cause: 'end' });
          let linkOut = document.querySelector('.linkOut');
          if (linkOut) {
            linkOut.classList.remove('isShow');
          }

          let finished = () => {
            eventsSrv.emit(PLAYER_EVENTS.ENDED);
            if (!Config.get('params.imported')) {
              PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.ENDED);
            }
            if (vjsSrv.vjsPlayer.PrevNextButtonSrv.isActive('NextButton')) {
              vjsSrv.vjsPlayer.PrevNextButtonSrv.handleClick('NextButton', {
                pmEvent: PLAYER_EVENTS.PLAYING_NEXT
              });
            }
          };
          if (ads && ads.withAds && ads.hasPostRoll) {
            vjsSrv.vjsPlayer.addClass('custom-waiting-video');
            $logger.p('event').info('ended', 'waiting till post-roll will end');
            ads.off('finished', finished);
            ads.one('finished', finished);
            // todo: wait till postroll ends
          } else {
            finished();
          }
        }
      });

    vjsSrv.vjsPlayer
      .on('abort', (event) => {
        if (Config.get('eventsState.sourceChanging')) {
          return;
        }

        if ((vimmiPI.getActiveAsset() && vimmiPI.getActiveAsset().get('state.changingAudio')) || (Config.get('eventsState.ended') || Config.get('eventsState.error'))) {
          /** in case when we change audio lang and do need to send the event*/
          return;
        }

        $logger.p('event').info('abort', {
          duration: vjsSrv.vjsPlayer.duration(),
          currentTime: vjsSrv.vjsPlayer.currentTime(),
          eventsState: Config.get('eventsState'),
          state: vimmiPI.getActiveAsset() ? vimmiPI.getActiveAsset().get('state') : {}
        });

        let asset = vimmiPI.getActiveAsset();
        if (!Config.get('eventsState.loadstart')) {
          asset = PlayerSrv.prevAsset;
        }
        let error = new Error('video end');
        error.code = ErrorCodes.NET_ERR;
        Analytics.send('abort', {
          position: asset && !asset.isLive ? Tools.fixTo(asset.extraData.active_position, 0) || 0 : 0
        }, {
          asset: asset,
          error: error
        });
      });

    // in any other case the 'error' handler should be defined in play type handler ex: "MetadataClass"
    vjsSrv.vjsPlayer
      .on('error', (event) => {
        let asset = vimmiPI.getActiveAsset();
        let xhrError = asset?.extraData?.xhrError;

        let error = vjsSrv.vjsPlayer.internalError || vjsSrv.vjsPlayer
          .error();
        if (!error.code) {
          error.code = ErrorCodes.NET_ERR;
        }

        $logger.p('event').error('error', {
          duration: vjsSrv.vjsPlayer.duration(),
          currentTime: vjsSrv.vjsPlayer.currentTime(),
          eventsState: Config.get('eventsState'),
          state: vimmiPI.getActiveAsset() ? vimmiPI.getActiveAsset().get('state') : {},
          error: error,
          xhrError
        });

        vimmiPI.concurStop();
        vjsSrv.vjsPlayer.removeClass('custom-waiting-video');
        Analytics.send('media_error', {}, {
          error: error
        });
        /** todo: check last playback error*/
        if (error.errCode !== 800 /*CDN_ERR*/ && error.name !== 'playbackUrls' && (!xhrError || (xhrError?.status >= 200 && xhrError?.status < 400) || xhrError?.status >= 500)) {
          let errorHandlerTimeoutId = null;
          if (xhrError?.status >= 500) {
            setTimeout(() => {
              errorHandlerTimeoutId = vimmiPI.handlePlaybackError(error);
            }, 2000);
          } else {
            errorHandlerTimeoutId = vimmiPI.handlePlaybackError(error);
          }
          if (asset) {
            asset.set('extraData.errorHandlerTimeoutId', errorHandlerTimeoutId);
          }
        } else {
          eventsSrv.emit(PLAYER_EVENTS.ERROR);
          if (!Config.get('params.imported')) {
            PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.ERROR);
          }
        }
        Config.set('eventsState.error', true);
      });

    // for detect dbclick
    let timeStamp = Date.now();
    vjsSrv.vjsPlayer
      .on('dblclick', (event) => {
        if (ads.playing()) {
          return;
        }
        if (!Config.get('params.controls')) {
          return;
        }
        $logger.p('event').info('dblclick', {
          duration: vjsSrv.vjsPlayer.duration(),
          currentTime: vjsSrv.vjsPlayer.currentTime(),
          eventsState: Config.get('eventsState'),
          state: vimmiPI.getActiveAsset() ? vimmiPI.getActiveAsset().get('state') : {}
        });
        if (Config.get('params.audio_tag') || (vimmiPI.getActiveAsset() && Restriction.isRestricted(vimmiPI.getActiveAsset()))) {
          return;
        }
        changeFullscreenMode(event);
      });
    vjsSrv.vjsPlayer
      .on('touchend', (event) => {
        if (!Config.get('params.controls')) {
          return;
        }
        if (Config.get('params.audio_tag') || Restriction.isRestricted(vimmiPI.getActiveAsset())) {
          return;
        }
        if (timeStamp + 250 >= Date.now() && !ads.playing()) {
          changeFullscreenMode(event);
        }
        timeStamp = Date.now();
      });

    function changeFullscreenMode(event) {
      let controlBarClicked = false;
      (event?.path || []).map(item => {
        if (item.classList !== undefined && item.classList.length > 0) {
          if (item.classList.contains('vjs-control-bar')) {
            controlBarClicked = true;
          }
        }
      });

      if (controlBarClicked) {
        return;
      }

      if (vjsSrv.vjsPlayer
        .isFullscreen()) {
        vjsSrv.vjsPlayer
          .exitFullscreen();
      } else {
        vjsSrv.vjsPlayer
          .requestFullscreen();
      }
    }
  }

  get asset() {
    return vimmiPI.getActiveAsset() || null;
  }

  //
  // get position() {
  //   return vjsSrv.vjsPlayer.currentTime() || 0
  // }
  //
  // get duration() {
  //   return vjsSrv.vjsPlayer
  //     .duration() || 0
  // }

  async playVideo(options) {
    vjsSrv.vjsPlayer.addClass('custom-waiting-video');
    let asset = PlayerSrv.transformToAsset(Object.assign({}, Config.get('params'), options));
    if (vimmiPI.getActiveAsset()) {
      vimmiPI.concurStop();
    }
    if (asset?.playerOptions?.preloadMode === 'none' && !vimmiPI.getActiveAsset()) { // only for the first video
      vjsSrv.vjsPlayer.waitingAsset = asset;

      if ((asset.adTag || asset.adVast) && !Restriction.isRestricted(asset)) {
        if (!ads.isAdInit) {
          await ads.clear();
          await ads.handleAds({ adTag: asset.adTag, adVast: asset.adVast }, asset.adVolume, false, asset.isLive);
          asset.adsPreloaded = true;
        }
      }
      vjsSrv.vjsPlayer.removeClass('custom-waiting-video');
      PostersManager.setCurrentPoster(asset);
    } else {
      this.playerWrapper.play(asset, false);
    }
  }

  pauseAds() {
    if (ads.playing()) {
      ads.pauseAds();
    }
  }

  /**
   * @method configure
   * @param {Object} configs the config object
   * @param {Object} configs.analytics the config object for analytics modules (supported only 'buh')
   * @param {Boolean} configs.analytics.debug define does it needs to show all logs from analytics plugin
   * @param {Object} configs.analytics.buh  config for BUH analytics
   * @param {Number} configs.analytics.buh.chugap interval to send chu events (seconds)
   * @param {String} configs.analytics.buh.ep the analytics domain endpoint
   * @param {String} configs.analytics.buh.customPath the analytics path (to rewrite default "/event/evn/")
   * @param {Boolean} configs.analytics.buh.nstk  ?
   * @param {Boolean} configs.analytics.buh.buhsy  // disable sending BUH analytics when come false from ams // false = disable, true = enable
   * @param {Boolean} configs.analytics.buh.olsy  Use old analytics system (CM). Default is true. // false = disable, true = enable
   * @param {Boolean} configs.analytics.buh.package  send events in packages
   * @param {Number} configs.analytics.buh.egap  Max gap in seconds to send events. Default 30 seconds. // used for sending packages
   * @param {Object} configs.analytics.buh.packageOpt   config for events packaging
   * @param {Number} configs.analytics.buh.packageOpt.maxEvents   max events in 1 package
   * @param {Number} configs.analytics.buh.packageOpt.minEvents   min events in 1 package
   * @param {Boolean} configs.analytics.buh.stkwrite   ?
   * @param {Object} [configs.player_conf]  the config object for the player itself
   * @param {Boolean} configs.player_conf.restrictions  define does the player needs to handle restrictions
   * @param {Boolean} configs.player_conf.vimmi_copyright  show vimmi context menu instead bro  wser default
   * @param {Boolean} configs.player_conf.vimmi_copyright  show vimmi context menu instead bro  wser default
   * @param {Object} configs.portal   the config object with portal info and credentials
   * @param {String} configs.portal.server  the AMS backend domain
   * @param {String} configs.portal.customer  customer ('ais'|....)
   * @param {Array} configs.portal.restrictedDevices  list of devices for restrictions (to send to AMS as "d" query parameter)
   * @param {'mob'|'stb'|'web'} configs.portal.platform
   * @param {Object} [configs.portal.concurrency]  config for concurrency took from AMS
   * @param {Array} configs.portal.concurrency.content  list of watched content ex: ['not_free']
   * @param {String} configs.portal.concurrency.cgrp  - ?
   * @param {String} configs.portal.concurrency.ep  - domain to send the concurrency status
   * @param {Number} configs.portal.concurrency.cgap  - interval in seconds for keepalive requests
   * @param {String} configs.portal.Device-Info  - formatted string with device info ex: "com.vimmi.ais.portal, Windows + Chrome, AppVersion: 4.7.15, 10, Language: tha"
   * @param {String} configs.portal.language  - iso-2 or iso-3 lang code ex: tha, eng, basically used for request header preparing
   *
   * @param {String} configs.portal.app_version  - version of app
   * @param {String} configs.portal.app_info  - info about app. based on "customer" and "platform". used for analytics ex:  "AIS-WEB"
   * @param {String} configs.portal.public_app_key  - unknown goal key ex: "vimmiCB1D0336"
   *
   * @param {String} configs.portal.device_key  - unknown goal string, ex: "com.vimmi.ais.portal"
   * @param {String} configs.portal.device_id  - unique device id (Base generated by FingerprintJs)
   * @param {String} configs.portal.buhDeviceId  - unique random string stored in Web, used as part of device id to more unique device identification in BUH (ios issue)
   *
   * @param {String} configs.portal.private_id  - AMS user private id
   * @param {String} configs.portal.sid  -  AMS user session id
   * @param {String} configs.portal.user_id  -  AMS user id
   * @param {String} configs.portal.user_lang  - iso-2 or iso-3 lang code ex: tha, eng, basically used for analytics
   * @param {String} configs.portal.temp_user_id  - random hash string, used for analytics
   * @param {String} configs.portal.classification  - user classification
   * @param {string} configs.portal.preferred_audio_lang  - iso-2 or iso-3 lang code, to using as default for content
   *
   * @param {Object} configs.portal.metadata  - Custom metadata query params, to overwrite ex: cdn=redfox to cdn=redfox-https
   * @param {Object} configs.customPlayTemplateHeaders  - Custom headers for playtemplate request
   *
   *
   * */
  configure(configs) {
    configs = configs || {};
    if (configs.player_conf) {
      Config.set('params', Object.assign(Config.get('params'), configs.player_conf));
    }
    if (!Config.get('params.imported') && configs.thirdLocation && Config.get('params.tl')) {
      if (configs.thirdLocation.retryRequest) {
        fetchAMS.retry();
      } else if (configs.thirdLocation.cancelRequest) {
        fetchAMS.cancel();
      } else {
        thirdLocation.set('domains', configs.thirdLocation.domains);
        thirdLocation.set('activeDomain', configs.thirdLocation.activeDomain);
        thirdLocation.set('nRetries', configs.thirdLocation.nRetries);
        thirdLocation.set('nNonPriorityRetries', configs.thirdLocation.nNonPriorityRetries);
        thirdLocation.set('nDistroRetries', configs.thirdLocation.nDistroRetries);
        thirdLocation.set('confRetryDelay', configs.thirdLocation.confRetryDelay);
        thirdLocation.set('confAttempts', configs.thirdLocation.confAttempts);
        thirdLocation.set('maxReq', configs.thirdLocation.maxReq);
      }
    }

    if (configs.portal) {
      vimmiPI.debug($logger.config.get('debug.level'));
      vimmiPI.conf({
        backend: configs.portal.server, // important
        customer: configs.portal.customer, // important

        deviceRestrictions: configs.portal.restrictedDevices,
        platform: configs.portal.platform || ['web'], // One of: 'mob' (Mobile device), 'stb' or 'web'.
        concurrency: configs.portal.concurrency,
        appInfo: {
          'Api-Version': '2.8.2',
          'Device-Info': configs.portal['Device-Info'],
          language: configs.portal.language, // important
          app_version: configs.portal.app_version // important
        },
        authInfo: {
          public_app_key: configs.portal.public_app_key,
          device_key: configs.portal.device_key,
          privateId: configs.portal.private_id,
          device_id: configs.portal.device_id,
          sid: configs.portal.sid,
          dat: configs.portal.dat
        },
        customCDNQuery: configs.portal.metadata,
        // ams: {
        //   portal: {
        //     player: {
        //       metadata: configs.portal.metadata
        //     }
        //   }
        // },
        customPlayTemplateHeaders: configs.customPlayTemplateHeaders || null
      });
      for (let key in configs) {
        Config.set(`params.${key}`, configs[key]);
      }
      if (configs.portal?.language && Config.get('platform') === 'stv') {
        vjsSrv.vjsPlayer.language(configs.portal?.language);
        if (vjsSrv.vjsPlayer.error()) {
          let error = vjsSrv.vjsPlayer.error();
          if (vjsSrv.vjsPlayer.errorDisplay) {
            vjsSrv.vjsPlayer.errorDisplay.contentEl().innerHTML = vjsSrv.vjsPlayer.localize(error.message);
          }
        }
        if (Preview.modal) {
          Preview.updateText()
        }
      }
    }
    Analytics.configure(configs);
  }

  /**
   * @param track{AudioTrack|Object}
   * @param track.id {string} id of track
   * @param track.label {string} label of track
   * @param track.language {string} language of track
   * @param trackType {'default'|'custom'} type of track
   *
   * */
  changeAudio(track) {
    if (track) {
      vjsSrv.vjsPlayer.TechSrv.changeTrack(track);
    }
  }

  setRestriction(options) {
    let asset = vimmiPI.getActiveAsset() || Restriction.currentAsset;
    if (asset) {
      asset.restricted = options.restricted;
      if (Restriction.isRestricted(asset)) {
        Restriction.show(asset);
        vjsSrv.vjsPlayer.reset();
      } else {
        Restriction.hide();
        vjsSrv.vjsPlayer.play();
      }
    }
  }

  togglePreview(options) {
    if (options.preview && options.preview_info) {
      vimmiPI.getActiveAsset().set('preview', true);
      vimmiPI.getActiveAsset().set('preview_info', options.preview_info);
      Preview.show(vimmiPI.getActiveAsset());
    } else {
      vimmiPI.getActiveAsset().set('preview', false);
      vimmiPI.getActiveAsset().set('preview_info', null);
      Preview.hide();
    }
  }

  //
  // seekTo(position) {
  //   vjsSrv.vjsPlayer.currentTime(position);
  // }

  hide() {
    vjsSrv.vjsPlayer.addClass('vjs-hidden');
    // if (document.querySelector('.rem-popup:not(.vjs-hidden)')) {
    //   remoteControl.handleBack();
    // }
  }

  show() {
    vjsSrv.vjsPlayer.removeClass('vjs-hidden');
  }


  requestFullscreen() {
    if (!vjsSrv.vjsPlayer.isFullscreen()) {
      vjsSrv.vjsPlayer
        .requestFullscreen();
    }
  }

  exitFullscreen() {
    if (vjsSrv.vjsPlayer.isFullscreen()) {
      vjsSrv.vjsPlayer
        .exitFullscreen();
    }
  }

  async reset() {
    const status = await vjsSrv.resetPlayer();
    if (vjsSrv.vjsPlayer.error()) {
      vjsSrv.vjsPlayer.error(null);
    }
    Preview.hide();
    vjsSrv.vjsPlayer.removeClass('vjs-waiting');
    vjsSrv.vjsPlayer.removeClass('custom-waiting-video');
    return status;
  }

  controls = {
    play(userAction) {
      vjsSrv.onStarted(userAction);
      vjsSrv.vjsPlayer.play();
    },
    stop() {
      if (!vimmiPI.getActiveAsset().get('isLive')) {
        vjsSrv.vjsPlayer.currentTime(0);
      }
      vjsSrv.vjsPlayer.pause();
    },
    pause(userAction) {
      if (Config.get('eventsState.isCallPlay')) {
        vjsSrv.onPaused(userAction);
        vjsSrv.vjsPlayer.pause();
      }
    },
    mute() {
      if (vjsSrv.vjsPlayer.muted()) {
        // Config.set('params.muted', false);
        vjsSrv.vjsPlayer.muted(false);
      } else {
        // Config.set('params.muted', true);
        vjsSrv.vjsPlayer.muted(true);
      }
    },

    /**
     * @method controlsState to manipulate cotrols of player (hide/show/change)
     * @param event {Object} event
     * @param event.name {String} name of action
     * @param event.data {Object} configs the config object
     * */
    controlsState(event) {
      let options = event && event.data ? event.data : {};
      switch (event.name) {
        case 'disableControls':
          vjsSrv.controls(false);
          if (vjsSrv.vjsPlayer.WrapperBigPlayBtn) {
            vjsSrv.vjsPlayer.WrapperBigPlayBtn.hide();
          }

          break;
        case 'enableControls':
          vjsSrv.controls(true);
          if (vjsSrv.vjsPlayer.WrapperBigPlayBtn) {
            vjsSrv.vjsPlayer.WrapperBigPlayBtn.show();
          }
          break;
        case 'enablePrev':
          vjsSrv.vjsPlayer.PrevNextButtonSrv.enable('PrevButton');
          break;
        case 'disablePrev':
          vjsSrv.vjsPlayer.PrevNextButtonSrv.disable('PrevButton');
          break;

        case 'showPrev':
          vjsSrv.vjsPlayer.PrevNextButtonSrv.show('PrevButton');
          break;
        case 'hidePrev':
          vjsSrv.vjsPlayer.PrevNextButtonSrv.disable('PrevButton');
          vjsSrv.vjsPlayer.PrevNextButtonSrv.hide('PrevButton');
          break;

        case 'loadPrev':
        case 'loadNext':
          let internalAsset = PlayerSrv.transformToAsset(Object.assign({}, Config.get('params'), options));

          let button = 'NextButton';
          let suggest = 'SuggestVideoNext';
          if (event.name === 'loadPrev') {
            button = 'PrevButton';
            suggest = 'SuggestVideoPrev';
            // PostersManager.setPrevPoster(internalAsset);
          } else {
            // PostersManager.setNextPoster(internalAsset);
          }

          internalAsset.extraData.click_to_media_start = internalAsset.extraData.click_to_media_start || Date.now();


          if (vimmiPI.getActiveAsset() && vimmiPI.getActiveAsset().get('metadataLang')) {
            internalAsset.metadataLang = vimmiPI.getActiveAsset().get('metadataLang');
          }

          vimmiPI.getByMediaId(internalAsset, false).then((asset) => {
            asset.set('extraData.click_to_media_end', Date.now());
            vjsSrv.vjsPlayer.PrevNextButtonSrv.setAsset(button, asset);
            vjsSrv.vjsPlayer.PrevNextButtonSrv.enable(button);
            vjsSrv.vjsPlayer.PrevNextButtonSrv.show(button);
          }).catch((error) => {
            $logger.p('vimmiPI/getByMediaId').error(error);
          });

          break;

        case 'enableNext':
          vjsSrv.vjsPlayer.PrevNextButtonSrv.enable('NextButton');
          break;
        case 'disableNext':
          vjsSrv.vjsPlayer.PrevNextButtonSrv.disable('NextButton');
          break;
        case 'showNext':
          vjsSrv.vjsPlayer.PrevNextButtonSrv.show('NextButton');
          break;
        case 'hideNext':
          vjsSrv.vjsPlayer.PrevNextButtonSrv.disable('NextButton');
          vjsSrv.vjsPlayer.PrevNextButtonSrv.hide('NextButton');
          break;
        case 'seekTo':
          if (options.position) {
            vjsSrv.vjsPlayer.currentTime(options.position);
          }
          break;
      }
    },


    /**
     * @method cutAPI to enable/disable cutting api
     * @param event {Object} event
     * @param event.type {'disable'|'enable'} type of action
     * @param event.data {Object}  the config object
     * @param event.data.start_time {Number} start time of selector
     * @param event.data.duration {Number} initial duration
     * @param event.data.max_duration {Number} max duration
     * @param event.data.min_duration {Number} mix duration
     * */
    cutAPI(event) {
      if (event && event.type && event.data) {
        if (event.type === 'enable') {
          if (!vjsSrv.vjsPlayer.CutVideoSrv) {
            vjsSrv.vjsPlayer.CutVideoSrv = new CutVideoService();
          }
          vjsSrv.vjsPlayer.CutVideoSrv.enable(event.data);
        } else if (event.type === 'disable') {
          if (vjsSrv.vjsPlayer.CutVideoSrv) {
            vjsSrv.vjsPlayer.CutVideoSrv.disable(event.data);
          }
        }
      }
    },

    /**
     * @method cutAPI to enable/disable cutting api
     * @param event {Object} event
     * @param event.type {'disable'|'enable'} type of action
     * @param event.data {Object}  the config object
     * @param event.data.width {number} width of snapshot frame
     * @param event.data.height {number} height of snapshot frame
     * */
    snapshotAPI(event) {
      if (event && event.type) {
        if (event.type === 'enable') {
          if (!vjsSrv.vjsPlayer.SnapshotSrv) {
            vjsSrv.vjsPlayer.SnapshotSrv = new SnapshotService();
          }
          vjsSrv.vjsPlayer.SnapshotSrv.enable(event.data);
        } else if (event.type === 'disable') {
          if (vjsSrv.vjsPlayer.SnapshotSrv) {
            vjsSrv.vjsPlayer.SnapshotSrv.disable(event.data);
          }
        }
      }
    }
  };

  async destroy() {
    if (Config.get('eventsState.started')) {
      if (!Config.get('eventsState.ended')) {
        Analytics.send('chu', {}, { force: true });
        Analytics.send('stop', { stop_cause: 'close' });
      }
      vimmiPI.concurStop();
    }
    offSubscribers();
    eventsSrv.off('_internal-play');
    eventsSrv.off('app_representation_loads');
    eventsSrv.off('videoBitrateChanged');

    this.playerWrapper.destroy();
    await vjsSrv.resetPlayer();
    Preview.hide();
    Restriction.hide();
    vjsSrv.destroy();
    Config.set('eventsState', {
      isStartPlay: false,
      prevUserActiveState: false,
      loadstart: false,
      waiting: false,
      loadedmetadata: false,
      loadeddata: false,
      ended: false,
      started: false,
      error: false
    });
  }
}